import { formatQuantity } from '@kronos/domain'
import type { InventoryItem } from '../../data/inventory'
import { Link } from '../shell/Link'
import { ItemThumb } from './ItemArt'
import { StockBadges, StockMeter } from './Stock'
import { whenText } from './shared'

/**
 * The compact view: one row per item, for scanning a long list by name — the
 * Shopify product list rather than the gallery. Still a picture on every row.
 *
 * One layout at every width: a CSS grid lays the same row out as columns at
 * 900px and up, and folds the middle columns away on a phone. Each row is a
 * single target with a chevron, so it reads as pressable rather than as text
 * with a link hidden in it.
 */
export function InventoryTable({ items }: { items: readonly InventoryItem[] }): JSX.Element {
  return (
    <div className="panel">
      <div className="inv-list-head" aria-hidden="true">
        <span>Item</span>
        <span>Category</span>
        <span className="num">On the shelf</span>
        <span className="num">Fitted</span>
        <span>Last received</span>
      </div>
      <div className="row-list">
        {items.map((item) => (
          <Link key={item.id} to={{ name: 'item', id: item.id }} className="row-item inv-list-row">
            <span className="inv-list-row__item">
              <ItemThumb imageId={item.imageId} category={item.category} name={item.name} size={44} />
              <span className="row-item__main">
                <span className="row-item__title">{item.name}</span>
                <span className="row-item__meta">
                  {/* The category is its own column at a desk; on a phone it leads the line. */}
                  <span className="inv-list-row__phone-meta">
                    {item.category}
                    {item.category && (item.partNumber || item.supplier) ? ' · ' : ''}
                  </span>
                  {item.partNumber || item.supplier}
                </span>
                <StockBadges item={item} />
              </span>
            </span>
            <span className="inv-list-row__cat muted">{item.category || '—'}</span>
            <span className="inv-list-row__shelf">
              <span className="figure">{formatQuantity(item.onShelf, item.unit)}</span>
              <StockMeter item={item} />
            </span>
            <span className="inv-list-row__fitted num muted">{formatQuantity(item.fitted, item.unit)}</span>
            <span className="inv-list-row__when muted">{item.lastReceivedAt ? whenText(item.lastReceivedAt) : 'Never'}</span>
          </Link>
        ))}
      </div>
    </div>
  )
}
